import { useState, useEffect } from "react";
import { FormInput } from "components/ui/form/FormInput";
import { useForm } from "react-hook-form";
import Form from "components/ui/form/Form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { emailAddress, impersonateToken } from "utilities/formSchemas";
import { Button } from "components/ui/button/Button";
import { ImpersonateData, usePostImpersonateMutation } from "./impersonateService";
import { ErrorType } from "utilities/error";
import { setCredentials, setIsImpersonating } from "features/authSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ToastMessage from "components/ui/toast-message/ToastMessage";
import { shutdownFullStory } from "utilities/full-story";

const formSchema = z.object({
  toEmailAddress: emailAddress,
  token: impersonateToken,
});
export default function Impersonate() {
  const {
    register, handleSubmit, formState: { errors } } = useForm<{ toEmailAddress: string; token: string }>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        toEmailAddress: "",
        token: ""
      },
    });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState(0);
  const [showError, setShowError] = useState(false);
  const [sendRequest, { isSuccess, data, isLoading }] = usePostImpersonateMutation();
  const onSubmit = (data: ImpersonateData) => {
    // Clear previous errors before making new request
    setShowError(false);
    setError(0);

    return sendRequest(data)
      .unwrap()
      .catch((error: ErrorType) => {
        setError(error.status);
        setShowError(true);
      });
  };
  if (isSuccess && data) {
    dispatch(
      setCredentials({
        ...data,
      })
    );
    dispatch(setIsImpersonating(true));
    shutdownFullStory();
    navigate("/my-loans", { replace: true });
  }

  useEffect(() => {
    if (showError) {
      const timer = setTimeout(() => {
        setShowError(false);
      }, 4100);
      return () => clearTimeout(timer);
    }
  }, [showError]);

  const getErrorMessage = (errorStatus: number) => {
    if (errorStatus === 401) {
      return "Invalid token";
    } else if (errorStatus === 404) {
      return "The user does not exist in the system";
    } else if (errorStatus === 400) {
      return "User has not completed the registration process";
    }
    return "An error occurred. Please try again.";
  };

  return (
    <>
      {showError && (
        <ToastMessage
          message={getErrorMessage(error)}
          state="error"
          timeout={4000}
          position="top-center"
        />
      )}
      <Form onSubmit={handleSubmit(async (data: ImpersonateData) => {
        await onSubmit({
          toEmailAddress: data.toEmailAddress,
          token: data.token,
        });
      })}
      >
        <FormInput
          formHandle={register("toEmailAddress")}
          inputProps={{
            name: "toEmailAddress",
            placeholder: "Enter emailAddress of the person to impersonate",
            label: "Email Address",
            errorMessage: errors.toEmailAddress?.message as string,
          }}
        />
        <FormInput
          formHandle={register("token")}
          inputProps={{
            name: "token",
            placeholder: "Enter impersonate token",
            label: "Impersonate Token",
            errorMessage: errors.token?.message as string,
          }}
        />
        <Button
          title={isLoading ? "Loading..." : "Continue"}
          className="login-btn"
          disabled={isLoading}
        />
      </Form>
    </>
  );
}